/**
 * @fileoverview Modal dialog listing all persistent highlights in the current PDF.
 * Each row has Time / Lead / Page / Excerpt and per-row Delete + Go-to buttons.
 * A "Delete All" button in the footer wipes the whole set after confirmation.
 * @module ui/PdfHighlightsListDialog
 */

import { BaseDialog } from './BaseDialog.js';
import { ExportDialog } from './ExportDialog.js';
import { ImportDialog } from './ImportDialog.js';
import { escapeHtml, urlFileName } from '../utils/jrfuncs.js';


const EXCERPT_MAX_CHARS = 140;


/**
 * Lists every highlight in the currently loaded PDF, with per-row
 * goto/delete actions and a delete-all footer button. Singleton.
 * @class
 * @extends BaseDialog
 */
export class PdfHighlightsListDialog extends BaseDialog {

    constructor(options = {}) {
        super({ idPrefix: 'pdfHighlightsListDialog' });
        this.viewer = options.viewer || null;
        // When true, a fresh editable note is added (and focused) as soon as
        // the dialog finishes opening — used by the Ctrl+L "add note" hotkey.
        this.autoAddNote = options.autoAddNote || false;
    }


    /** @override */
    getModalHtml() {
        const bodyHtml = `
            <div class="pdf-highlights-list-wrapper">
                <table class="table table-sm pdf-highlights-list-table">
                    <thead>
                        <tr>
                            <th class="col-time">Time</th>
                            <th class="col-page">Page</th>
                            <th class="col-lead">Lead</th>
                            <th class="col-excerpt">Excerpt</th>
                            <th class="col-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody id="${this.modalId}_tbody"></tbody>
                </table>
                <div class="pdf-highlights-list-empty" id="${this.modalId}_empty" style="display:none;">
                    No highlights yet in this PDF. Select some text and use <kbd>Ctrl+Y</kbd> or the right-click menu to add one.
                </div>
            </div>
        `;

        const footerHtml = `
            <button type="button" class="btn btn-danger me-auto" id="${this.modalId}_deleteAllBtn">
                <i class="bi bi-trash"></i> Delete All
            </button>
            <button type="button" class="btn btn-outline-secondary" id="${this.modalId}_addNoteBtn">
                <i class="bi bi-journal-plus"></i> Add Note
            </button>
            <button type="button" class="btn btn-outline-secondary" id="${this.modalId}_importBtn">
                <i class="bi bi-box-arrow-in-down"></i> Import
            </button>
            <button type="button" class="btn btn-outline-secondary" id="${this.modalId}_exportBtn">
                <i class="bi bi-box-arrow-up"></i> Export
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        `;

        return this.buildModalHtml({
            title: 'Highlights in this PDF',
            icon: 'bi-list-stars',
            bodyHtml,
            footerHtml,
            // the extra class lets pdfreader.css widen this dialog to near
            // full page width on viewports under 1600px (excerpt legibility)
            size: 'modal-xl pdf-highlights-list-modal'
        });
    }


    /** @override */
    setupEventListeners() {
        // Populate rows once on open. Subsequent updates (after delete) call
        // renderRows() directly — the delegated tbody listener keeps working
        // because we only replace innerHTML, not the tbody element itself.
        this.renderRows();

        const tbody = this.getElement('_tbody');
        if (tbody) {
            tbody.addEventListener('click', (e) => this._onTbodyClick(e));
            // Inline note editing uses delegated listeners (only innerHTML is replaced).
            // `blur` does not bubble, so we listen for `focusout`/`keydown` which do.
            tbody.addEventListener('focusout', (e) => this._onNoteCellBlur(e));
            tbody.addEventListener('keydown', (e) => this._onNoteCellKeydown(e));
        }

        const addNoteBtn = this.getElement('_addNoteBtn');
        if (addNoteBtn) {
            addNoteBtn.addEventListener('click', () => this._onAddNoteClick());
        }

        const deleteAllBtn = this.getElement('_deleteAllBtn');
        if (deleteAllBtn) {
            deleteAllBtn.addEventListener('click', () => this._onDeleteAllClick());
        }

        const exportBtn = this.getElement('_exportBtn');
        if (exportBtn) {
            exportBtn.addEventListener('click', () => this._onExportClick());
        }

        const importBtn = this.getElement('_importBtn');
        if (importBtn) {
            importBtn.addEventListener('click', () => this._onImportClick());
        }
    }


    /** @override */
    onAfterShow() {
        // The Ctrl+L hotkey opens this dialog already in "add a note" mode:
        // create a blank note and focus its editable cell so the user can type
        // immediately. Deferred to onAfterShow so focus lands after the modal
        // transition completes. The focus scrolls to the new note, so the
        // current-page scroll below would fight it — skip it in that mode.
        if (this.autoAddNote) {
            this._onAddNoteClick();
            return;
        }
        this._scrollToCurrentPageRow();
    }


    /**
     * Scrolls the list to the first row belonging to the viewer's current page
     * (rows the renderer marked with the -currentpage class). Deferred to
     * onAfterShow because scrollIntoView is a no-op while the modal is hidden.
     * @private
     */
    _scrollToCurrentPageRow() {
        const tbody = this.getElement('_tbody');
        const row = tbody ? tbody.querySelector('.pdf-highlights-list-row-currentpage') : null;
        if (row) {
            // 'center' keeps the row clear of the sticky table header.
            row.scrollIntoView({ block: 'center' });
        }
    }


    /**
     * Rebuilds the tbody contents from the viewer's current highlight list,
     * and toggles the empty-state message.
     * @private
     */
    renderRows() {
        const tbody = this.getElement('_tbody');
        const empty = this.getElement('_empty');
        if (!tbody) return;

        const highlights = this.viewer ? this.viewer.getAllHighlights() : [];

        if (highlights.length === 0) {
            tbody.innerHTML = '';
            if (empty) empty.style.display = '';
            return;
        }
        if (empty) empty.style.display = 'none';

        tbody.innerHTML = highlights.map(h => this._rowHtml(h)).join('');
    }


    /**
     * Delegated click handler for goto and per-row delete buttons.
     * @private
     */
    async _onTbodyClick(e) {
        const trigger = e.target.closest('[data-action]');
        if (!trigger) return;
        const action = trigger.dataset.action;
        const id = trigger.dataset.highlightId;
        if (!id) return;

        if (action === 'goto') {
            e.preventDefault();   // anchor links use href="#"
            const highlight = this._findHighlight(id);
            if (!highlight) return;
            // Close the dialog before navigating so the user can see the page.
            this.resolve(null);
            await this.viewer.goToHighlight(highlight);
        } else if (action === 'delete') {
            if (this.viewer) {
                this.viewer.removeHighlight(id);
            }
            this.renderRows();
        }
    }


    /**
     * Creates a new freeform note (stamped with the viewer's current page + lead),
     * re-renders the table, and focuses the new note's editable Excerpt cell so the
     * user can type immediately.
     * @private
     */
    _onAddNoteClick() {
        if (!this.viewer) return;
        const id = this.viewer.addNote();
        this.renderRows();

        const tbody = this.getElement('_tbody');
        const cell = tbody ? tbody.querySelector(`.pdf-note-edit[data-highlight-id="${CSS.escape(id)}"]`) : null;
        if (!cell) return;
        cell.focus();
        // Place the caret at the end of the (initially empty) cell.
        const range = document.createRange();
        range.selectNodeContents(cell);
        range.collapse(false);
        const sel = window.getSelection();
        if (sel) {
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }


    /**
     * Saves an inline-edited note's text on blur (delegated via focusout).
     * @private
     */
    _onNoteCellBlur(e) {
        const cell = e.target.closest('.pdf-note-edit');
        if (!cell || !this.viewer) return;
        const id = cell.dataset.highlightId;
        if (!id) return;
        this.viewer.updateNoteText(id, cell.textContent.trim());
    }


    /**
     * Inline-edit keyboard handling: Enter commits (via blur) and closes the
     * dialog, Escape reverts to the last-saved text. Both bubble, so this is
     * delegated on the tbody.
     * @private
     */
    _onNoteCellKeydown(e) {
        const cell = e.target.closest('.pdf-note-edit');
        if (!cell) return;
        if (e.key === 'Enter') {
            e.preventDefault();   // commit instead of inserting a newline
            cell.blur();          // focusout handler persists the edited text
            this.resolve(null);   // close the dialog once the note is committed
        } else if (e.key === 'Escape') {
            e.preventDefault();
            const note = this._findHighlight(cell.dataset.highlightId);
            cell.textContent = note ? (note.text || '') : '';
            cell.blur();
        }
    }


    /**
     * Confirms then deletes all highlights, refreshing the table.
     * @private
     */
    _onDeleteAllClick() {
        if (!this.viewer) return;
        const count = this.viewer.getAllHighlights().length;
        if (count === 0) return;
        const ok = confirm(`Delete all ${count} highlight${count === 1 ? '' : 's'} in this PDF? This cannot be undone.`);
        if (!ok) return;
        this.viewer.deleteAllHighlights();
        this.renderRows();
    }


    /**
     * Exports all highlights for the current PDF as a JSON file (or clipboard) via ExportDialog.
     * @private
     */
    async _onExportClick() {
        if (!this.viewer) return;
        const data = this.viewer.getHighlightsExportData();
        if (!data.highlights.length) {
            alert('There are no highlights to export.');
            return;
        }
        await ExportDialog.showExportDialog({
            title: 'Export highlights',
            infoText: 'Save or copy the highlights for this PDF as JSON. You can import the file back later from this dialog.',
            contentJson: data,
            defaultFilename: this._exportFilenameBase(),
            defaultFormat: 'json',
            formatConverters: {
                json: {
                    label: 'JSON',
                    fileExtension: '.json',
                    mimeType: 'application/json',
                    converter: (content) => JSON.stringify(content, null, 2)
                }
            }
        });
    }


    /**
     * Imports highlights from pasted JSON or a .json file via ImportDialog, merging them into the
     * current PDF (duplicates skipped), then refreshes the list.
     * @private
     */
    async _onImportClick() {
        if (!this.viewer) return;

        const text = await ImportDialog.showImportDialog({
            title: 'Import highlights',
            infoText: 'Paste highlight JSON, or choose a .json file previously exported from this dialog. '
                + 'Imported highlights are merged into the current PDF (duplicates are skipped).',
            placeholder: 'Paste highlight JSON here…',
            acceptFileTypes: '.json',
            importButtonText: 'Import',
            contentParser: async (raw) => this._validateImportText(raw)
        });
        if (text == null) return;   // cancelled

        let parsed;
        try {
            parsed = JSON.parse(text);
        } catch (e) {
            alert(`Import failed: the text is not valid JSON (${e.message}).`);
            return;
        }

        const result = this.viewer.importHighlightsData(parsed);
        if (result.error) {
            alert(`Import failed: ${result.error}`);
            return;
        }
        this.renderRows();

        let msg = `Imported ${result.added} highlight${result.added === 1 ? '' : 's'}.`;
        if (result.skipped) {
            msg += ` Skipped ${result.skipped} (duplicate or invalid).`;
        }
        alert(msg);
    }


    /**
     * Validates pasted/loaded import text and returns a human-readable diagnostic for the import
     * dialog (an "ERROR:"-prefixed message marks it as invalid).
     * @param {string} raw
     * @returns {string}
     * @private
     */
    _validateImportText(raw) {
        const trimmed = (raw || '').trim();
        if (!trimmed) return '';
        let parsed;
        try {
            parsed = JSON.parse(trimmed);
        } catch (e) {
            return `ERROR: Not valid JSON — ${e.message}`;
        }
        const arr = Array.isArray(parsed)
            ? parsed
            : (parsed && Array.isArray(parsed.highlights) ? parsed.highlights : null);
        if (!arr) return 'ERROR: No "highlights" array found in the data.';
        const validCount = arr.filter(h => this.viewer && this.viewer.isValidImportedHighlight(h)).length;
        if (validCount === 0) {
            return `ERROR: Found ${arr.length} entr${arr.length === 1 ? 'y' : 'ies'}, but none are valid highlights.`;
        }
        let msg = `Found ${validCount} valid highlight${validCount === 1 ? '' : 's'}`;
        if (validCount < arr.length) {
            msg += `; ${arr.length - validCount} entr${arr.length - validCount === 1 ? 'y' : 'ies'} will be skipped as invalid`;
        }
        return msg + '.';
    }


    /**
     * Builds a default export filename base ("<pdfname>_highlights") from the current document.
     * @returns {string}
     * @private
     */
    _exportFilenameBase() {
        const viewer = this.viewer;
        // a url load: the url's file name; a local file: the name it was loaded under (a plain name,
        // never parsed as a url)
        let fileName = '';
        if (viewer && viewer.currentUrl) {
            fileName = urlFileName(viewer.currentUrl, { decode: true });
        } else if (viewer && viewer.currentFileName) {
            fileName = viewer.currentFileName;
        }
        const namePart = fileName.replace(/\.pdf$/i, '');
        return namePart ? `${namePart}_highlights` : 'highlights';
    }


    /**
     * Locates a highlight by id within the viewer's current set.
     * @private
     */
    _findHighlight(id) {
        if (!this.viewer) return null;
        return this.viewer.getAllHighlights().find(h => h.id === id) || null;
    }


    /**
     * Renders a single row's HTML.
     * @private
     */
    _rowHtml(h) {
        const isNote = h.kind === 'note';
        // (stored records come from localStorage, which a settings backup can fill: escape every cell)
        const time = escapeHtml(this._formatTime(h.createdAt));
        const page = escapeHtml(h.pageNum != null ? h.pageNum : '');
        const idAttr = escapeHtml(h.id);
        const noun = isNote ? 'note' : 'highlight';

        // For notes the Lead link jumps to the note's page (no marks to flash, but it navigates).
        const leadInner = h.bookmarkLabel
            ? `<a href="#" class="pdf-highlights-list-link" data-action="goto" data-highlight-id="${idAttr}" title="Go to this ${noun}">${escapeHtml(h.bookmarkLabel)}</a>`
            : '<span class="text-muted">—</span>';

        let excerptInner;
        if (isNote) {
            // Inline-editable cell — clicking it just focuses (no data-action="goto"/"delete" match).
            excerptInner = `<span class="pdf-note-edit" contenteditable="true" data-action="edit-note" data-highlight-id="${idAttr}" data-placeholder="Add note…">${escapeHtml(h.text || '')}</span>`;
        } else {
            const excerptText = escapeHtml(this._truncate(h.text || '', EXCERPT_MAX_CHARS));
            excerptInner = `<a href="#" class="pdf-highlights-list-link" data-action="goto" data-highlight-id="${idAttr}" title="Go to this highlight">${excerptText}</a>`;
        }

        const isCurrentPage = !!(this.viewer && h.pageNum != null && h.pageNum === this.viewer.currentPage);
        const rowClass = (isNote ? ' pdf-highlights-list-row-note' : '')
            + (isCurrentPage ? ' pdf-highlights-list-row-currentpage' : '');
        const noteIcon = isNote ? '<i class="bi bi-journal-text pdf-note-icon" title="Note"></i> ' : '';

        return `
            <tr class="${rowClass.trim()}" data-highlight-id="${idAttr}">
                <td class="col-time">${time}</td>
                <td class="col-page">${page}</td>
                <td class="col-lead">${leadInner}</td>
                <td class="col-excerpt">${noteIcon}${excerptInner}</td>
                <td class="col-actions">
                    <button type="button" class="btn btn-sm btn-outline-danger" data-action="delete" data-highlight-id="${idAttr}" title="Delete this ${noun}">
                        <i class="bi bi-trash"></i>
                    </button>
                </td>
            </tr>
        `;
    }


    /** @private */
    _formatTime(ts) {
        if (!ts) return '';
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return '';
        // Local time, "YYYY-MM-DD HH:MM" form
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }


    /** @private */
    _truncate(s, n) {
        if (s.length <= n) return s;
        return s.substring(0, n) + '…';
    }


    /**
     * Shows the dialog (singleton).
     * @param {Object} [options={}]
     * @returns {Promise<*>}
     */
    static async showDialog(options = {}) {
        return BaseDialog.showSingleton(PdfHighlightsListDialog, options);
    }
}
